import { ConnectButton } from '@rainbow-me/rainbowkit'
import { useEffect, useMemo, useState } from 'react'
import { useAccount, useSwitchChain } from 'wagmi'
import { useBalances } from '../hooks/useBalances'
import { useApprovals } from '../hooks/useApprovals'
import { useDebounced } from '../hooks/useDebounced'
import { useQuote } from '../hooks/useQuote'
import { useSwap } from '../hooks/useSwap'
import { useTokenList } from '../hooks/useTokenList'
import { useWrap } from '../hooks/useWrap'
import { FLARE_CHAIN_ID, NATIVE_GAS_RESERVE, SWAP_DEFAULTS, flare } from '../lib/config'
import { applySlippage, formatAmount, formatExact, formatRate, parseAmount } from '../lib/format'
import { PERMIT_EXPIRATION_SECONDS } from '../lib/permit2'
import { DEFAULT_TOKENS, NATIVE_TOKEN, isNative, wrapDirection, type TokenMeta } from '../lib/tokens'
import { DegradedBanner } from './DegradedBanner'
import { QuoteError } from './QuoteError'
import { RouteBreakdown } from './RouteBreakdown'
import { SettingsPanel } from './SettingsPanel'
import { TokenSelect } from './TokenSelect'

const AMOUNT_DEBOUNCE_MS = 400

export function SwapCard() {
  const { address, chainId, isConnected } = useAccount()
  const { switchChain } = useSwitchChain()
  const list = useTokenList()

  // FLR -> USD₮0: native FLR is what most wallets on this chain hold.
  const [tokenIn, setTokenIn] = useState<TokenMeta>(NATIVE_TOKEN)
  const [tokenOut, setTokenOut] = useState<TokenMeta>(DEFAULT_TOKENS[2] as TokenMeta)
  const [input, setInput] = useState('')
  const [picking, setPicking] = useState<'in' | 'out' | null>(null)
  const [slippageBps, setSlippageBps] = useState<number>(SWAP_DEFAULTS.slippageBps)
  const [deadlineMinutes, setDeadlineMinutes] = useState<number>(SWAP_DEFAULTS.deadlineMinutes)
  const [showSettings, setShowSettings] = useState(false)

  const { balances, refetch: refetchBalances } = useBalances(list.tokens)
  const amountIn = parseAmount(input, tokenIn.decimals)
  // Raw units only mean something beside the token they were parsed for, so the two settle
  // together. Debouncing the amount alone let a flip pair the old amount with the new token
  // for 400 ms: 2,500 FLR re-read as 2.5e15 USD₮0, or 20 USD₮0 as 2e-11 FLR, each quoted.
  const pendingAmount = useMemo(
    () => (amountIn === null ? null : { token: tokenIn.address, amount: amountIn }),
    [tokenIn.address, amountIn]
  )
  const settledAmount = useDebounced(pendingAmount, AMOUNT_DEBOUNCE_MS)
  const debouncedAmount = settledAmount?.token === tokenIn.address ? settledAmount.amount : null

  // FLR against WFLR is a WNat deposit or withdraw, not a trade: nothing is quoted and the
  // router is not involved. Every other pair — including FLR against anything else — is.
  const wrapMode = wrapDirection(tokenIn, tokenOut)
  const quote = useQuote({
    tokenIn: wrapMode ? null : tokenIn.address,
    tokenOut: tokenOut.address,
    amount: debouncedAmount,
  })
  const approvals = useApprovals(wrapMode ? null : tokenIn.address, debouncedAmount)
  const swap = useSwap()
  const wrap = useWrap()

  const wrongChain = isConnected && chainId !== FLARE_CHAIN_ID
  const nativeIn = isNative(tokenIn)
  const balanceIn = balances[tokenIn.address]
  const overBalance = amountIn !== null && balanceIn !== undefined && amountIn > balanceIn
  // FLR is also the gas token. Spending all of it is allowed — the pre-signature estimate is
  // what refuses an unaffordable swap — but "Max" keeps some back and this says why.
  const leavesNoGas =
    nativeIn && amountIn !== null && balanceIn !== undefined && !overBalance && balanceIn - amountIn < NATIVE_GAS_RESERVE
  const maxIn =
    balanceIn === undefined ? undefined : nativeIn ? (balanceIn > NATIVE_GAS_RESERVE ? balanceIn - NATIVE_GAS_RESERVE : 0n) : balanceIn

  useEffect(() => {
    if (swap.phase.status === 'success' || wrap.phase.status === 'success') refetchBalances()
  }, [swap.phase.status, wrap.phase.status, refetchBalances])

  // A landed swap consumed the permit's nonce and left the allowance on chain: drop the
  // signature and read the allowance that replaced it.
  const { clearPermit, refetch: refetchApprovals } = approvals
  useEffect(() => {
    if (swap.phase.status !== 'success') return
    clearPermit()
    refetchApprovals()
  }, [swap.phase.status, clearPermit, refetchApprovals])

  // `current`, never `data`: the latter still holds the previous pair's quote for a beat
  // after a token or amount change. See useQuote.
  const data = quote.current
  const minimumOut = useMemo(
    () => (data ? applySlippage(BigInt(data.amountOut), slippageBps) : null),
    [data, slippageBps]
  )

  function flip() {
    setTokenIn(tokenOut)
    setTokenOut(tokenIn)
    setInput('')
    swap.reset()
    wrap.reset()
  }

  function select(token: TokenMeta) {
    if (picking === 'in') {
      if (token.address === tokenOut.address) setTokenOut(tokenIn)
      setTokenIn(token)
    } else if (picking === 'out') {
      if (token.address === tokenIn.address) setTokenIn(tokenOut)
      setTokenOut(token)
    }
    swap.reset()
    wrap.reset()
  }

  const needsApproval = approvals.needs !== null && approvals.needs.length > 0
  const nextApproval = approvals.needs?.[0] ?? null

  return (
    <div className="stack">
      <section className="card">
        <header className="card-head">
          <h2>Swap</h2>
          <button type="button" className="btn btn-ghost btn-small" onClick={() => setShowSettings((open) => !open)}>
            {slippageBps / 100}% · {deadlineMinutes}m
          </button>
        </header>

        {showSettings && (
          <SettingsPanel
            slippageBps={slippageBps}
            deadlineMinutes={deadlineMinutes}
            onSlippage={setSlippageBps}
            onDeadline={setDeadlineMinutes}
            onClose={() => setShowSettings(false)}
          />
        )}

        <div className="field">
          <div className="field-head">
            <span>You pay</span>
            {balanceIn !== undefined && maxIn !== undefined && (
              <button
                type="button"
                className="link"
                title={nativeIn ? `Max keeps ${formatExact(NATIVE_GAS_RESERVE, 18)} FLR back for gas` : undefined}
                onClick={() => setInput(formatExact(maxIn, tokenIn.decimals))}
              >
                Balance {formatAmount(balanceIn, tokenIn.decimals)}
              </button>
            )}
          </div>
          <div className="field-row">
            <input
              className="amount"
              inputMode="decimal"
              placeholder="0.0"
              value={input}
              onChange={(event) => setInput(event.target.value)}
            />
            <button type="button" className="token-button" onClick={() => setPicking('in')}>
              {tokenIn.symbol}
            </button>
          </div>
        </div>

        <div className="flip-row">
          <button type="button" className="flip" onClick={flip} aria-label="Swap direction">
            ↓
          </button>
        </div>

        <div className="field">
          <div className="field-head">
            <span>You receive</span>
            {balances[tokenOut.address] !== undefined && (
              <span className="muted">
                Balance {formatAmount(balances[tokenOut.address] as bigint, tokenOut.decimals)}
              </span>
            )}
          </div>
          <div className="field-row">
            <input
              className="amount"
              readOnly
              placeholder="0.0"
              value={
                wrapMode
                  ? amountIn !== null
                    ? formatExact(amountIn, tokenIn.decimals)
                    : ''
                  : data
                    ? formatAmount(data.amountOut, data.tokenOut.decimals, 8)
                    : ''
              }
            />
            <button type="button" className="token-button" onClick={() => setPicking('out')}>
              {tokenOut.symbol}
            </button>
          </div>
        </div>

        {wrapMode && (
          <div className="summary">
            <span>
              1 {tokenIn.symbol} = 1 {tokenOut.symbol}
            </span>
            <span className="muted">
              {wrapMode === 'wrap' ? 'Wraps' : 'Unwraps'} through WNat directly — nothing is routed, no approval needed.
            </span>
          </div>
        )}

        {!wrapMode && data && (
          <div className="summary">
            <span>
              {formatRate(
                data.amountIn,
                data.amountOut,
                data.tokenIn.decimals,
                data.tokenOut.decimals,
                tokenIn.symbol,
                tokenOut.symbol
              )}
            </span>
            {minimumOut !== null && (
              <span className="muted">
                Minimum received {formatAmount(minimumOut, data.tokenOut.decimals)} {tokenOut.symbol}
              </span>
            )}
            {nativeIn && (
              <span className="muted">Paid in FLR: the router wraps it in the same transaction, no approval needed.</span>
            )}
            {isNative(tokenOut) && (
              <span className="muted">Paid out in FLR: the router unwraps WFLR to you in the same transaction.</span>
            )}
            {quote.isFetching && !quote.isInitialLoad && <span className="muted">refreshing…</span>}
          </div>
        )}

        {leavesNoGas && (
          <p className="hint">
            This leaves less than {formatExact(NATIVE_GAS_RESERVE, 18)} FLR for gas. If the swap cannot be paid for, it is
            refused before you sign.
          </p>
        )}

        {!wrapMode && quote.isError && quote.error && (
          <QuoteError error={quote.error} onRetry={() => void quote.refetch()} />
        )}

        {!wrapMode && data && <DegradedBanner quote={data} />}

        <SwapButton
          isConnected={isConnected}
          wrongChain={wrongChain}
          onSwitch={() => switchChain({ chainId: flare.id })}
          hasAmount={amountIn !== null}
          overBalance={overBalance}
          symbolIn={tokenIn.symbol}
          wrap={
            wrapMode
              ? {
                  direction: wrapMode,
                  busy: wrap.isBusy,
                  onWrap: () => amountIn !== null && void wrap.execute(wrapMode, amountIn),
                }
              : null
          }
          quoting={quote.isInitialLoad}
          quoteReady={Boolean(data) && !quote.isError}
          approvals={{ needs: needsApproval, next: nextApproval, pending: approvals.pending }}
          onApprove={() => nextApproval && void approvals.approve(nextApproval)}
          swapBusy={swap.isBusy}
          swapPhase={swap.phase.status}
          onSwap={() => {
            if (!data) return
            void swap.execute(data, { slippageBps, deadlineSeconds: deadlineMinutes * 60, permit: approvals.permit })
          }}
        />

        {!wrapMode && nextApproval === 'permit2' && (
          <p className="hint">
            Signing is free: the Permit2 approval rides inside the swap transaction and covers {tokenIn.symbol} swaps
            for {PERMIT_EXPIRATION_SECONDS / 86_400} days.
          </p>
        )}

        {approvals.error && <p className="hint hint-error">{approvals.error.message.split('\n')[0]}</p>}

        {wrapMode ? (
          <SwapStatus
            phase={wrap.phase}
            noun={wrapMode === 'wrap' ? 'Wrap' : 'Unwrap'}
            onRequote={() => {}}
            onReset={wrap.reset}
          />
        ) : (
          <SwapStatus phase={swap.phase} noun="Swap" onRequote={() => void quote.refetch()} onReset={swap.reset} />
        )}

        {!wrapMode && data && <RouteBreakdown quote={data} />}
      </section>

      <TokenSelect
        open={picking !== null}
        list={list}
        balances={balances}
        exclude={picking === 'in' ? tokenOut.address : tokenIn.address}
        onSelect={select}
        onClose={() => setPicking(null)}
      />

      {!address && <p className="hint">Connect a wallet to swap. Quotes work without one.</p>}
    </div>
  )
}

function SwapButton(props: {
  isConnected: boolean
  wrongChain: boolean
  onSwitch: () => void
  hasAmount: boolean
  overBalance: boolean
  symbolIn: string
  /** Set when the pair is FLR/WFLR: the button wraps or unwraps instead of swapping. */
  wrap: { direction: 'wrap' | 'unwrap'; busy: boolean; onWrap: () => void } | null
  quoting: boolean
  quoteReady: boolean
  approvals: { needs: boolean; next: 'erc20' | 'permit2' | null; pending: 'erc20' | 'permit2' | null }
  onApprove: () => void
  swapBusy: boolean
  swapPhase: string
  onSwap: () => void
}) {
  if (!props.isConnected) {
    return (
      <div className="connect-slot">
        <ConnectButton />
      </div>
    )
  }
  if (props.wrongChain) {
    return (
      <button type="button" className="btn btn-primary" onClick={props.onSwitch}>
        Switch to Flare
      </button>
    )
  }
  if (!props.hasAmount) {
    return (
      <button type="button" className="btn btn-primary" disabled>
        Enter an amount
      </button>
    )
  }
  if (props.overBalance) {
    return (
      <button type="button" className="btn btn-primary" disabled>
        Not enough {props.symbolIn}
      </button>
    )
  }
  if (props.wrap) {
    const wrapping = props.wrap.direction === 'wrap'
    return (
      <button type="button" className="btn btn-primary" disabled={props.wrap.busy} onClick={props.wrap.onWrap}>
        {props.wrap.busy ? (wrapping ? 'Wrapping…' : 'Unwrapping…') : wrapping ? 'Wrap' : 'Unwrap'}
      </button>
    )
  }
  if (props.quoting) {
    return (
      <button type="button" className="btn btn-primary" disabled>
        Quoting…
      </button>
    )
  }
  if (!props.quoteReady) {
    return (
      <button type="button" className="btn btn-primary" disabled>
        No quote
      </button>
    )
  }
  if (props.approvals.needs && props.approvals.next) {
    const signing = props.approvals.next === 'permit2'
    const label = signing ? 'Sign Permit2 approval' : `Approve ${props.symbolIn} for Permit2`
    const busy = signing ? 'Sign in your wallet…' : 'Approving…'
    return (
      <button
        type="button"
        className="btn btn-primary"
        disabled={props.approvals.pending !== null}
        onClick={props.onApprove}
      >
        {props.approvals.pending !== null ? busy : label}
      </button>
    )
  }
  return (
    <button type="button" className="btn btn-primary" disabled={props.swapBusy} onClick={props.onSwap}>
      {props.swapBusy ? phaseLabel(props.swapPhase) : 'Swap'}
    </button>
  )
}

function phaseLabel(status: string): string {
  switch (status) {
    case 'building':
      return 'Building calldata…'
    case 'simulating':
      return 'Checking it will not revert…'
    case 'signing':
      return 'Confirm in your wallet…'
    case 'pending':
      return 'Swapping…'
    default:
      return 'Working…'
  }
}

function SwapStatus({
  phase,
  noun,
  onRequote,
  onReset,
}: {
  phase: ReturnType<typeof useSwap>['phase'] | ReturnType<typeof useWrap>['phase']
  /** What the transaction is, as the user thinks of it. */
  noun: 'Swap' | 'Wrap' | 'Unwrap'
  onRequote: () => void
  onReset: () => void
}) {
  if (phase.status === 'expired') {
    return (
      <div className="notice notice-warn">
        <div className="notice-title">That quote expired before it was signed</div>
        <p className="notice-body">
          Nothing was sent. The plan behind a quote is only valid for a short window, and the API refuses to re-price an
          expired one on its own — that would put your signature on a number you never saw. Refresh and look at the new
          quote before swapping.
        </p>
        <button
          type="button"
          className="btn btn-ghost btn-small"
          onClick={() => {
            onReset()
            onRequote()
          }}
        >
          Get a fresh quote
        </button>
      </div>
    )
  }

  if (phase.status === 'error') {
    return (
      <div className="notice notice-error">
        <div className="notice-title">{noun} failed</div>
        <p className="notice-body">{phase.error.message}</p>
        <button type="button" className="btn btn-ghost btn-small" onClick={onReset}>
          Dismiss
        </button>
      </div>
    )
  }

  if (phase.status === 'pending' || phase.status === 'success') {
    const url = `${flare.blockExplorers?.default.url}/tx/${phase.hash}`
    return (
      <div className={`notice ${phase.status === 'success' ? 'notice-good' : 'notice-warn'}`}>
        <div className="notice-title">
          {noun} {phase.status === 'success' ? 'confirmed' : 'submitted'}
        </div>
        <p className="notice-body">
          <a href={url} target="_blank" rel="noreferrer">
            View on the explorer
          </a>
        </p>
        {phase.status === 'success' && (
          <button type="button" className="btn btn-ghost btn-small" onClick={onReset}>
            Done
          </button>
        )}
      </div>
    )
  }

  return null
}
